import { useAvailableServicesForCategoryQuery } from '@dieterApi/snippet/useAvailableServicesForCategoryQuery';
import Autocomplete from '@components/ui/Autocomplete/Autocomplete';
import { Tickbox } from '@components/ui/Tickbox/Tickbox';
import { useShareTokenJwt } from '@app/hooks/useShareTokenJwt';
import { ISelectOption } from '@legalosApi/types/ISelectOption';
import React, { useMemo } from 'react';
import { Props } from './QuestionInput';
import { ScanPrefillBanner } from './ScanPrefillBanner';
import { SuggestServicePanel } from './SuggestServicePanel';

/**
 * Drop-in replacement for SelectSome that sources its option list from the
 * AuditPolicySnippet table (via `availableServicesForCategory`) rather than
 * from the LegalOS-supplied question options. Used for service-selection
 * questions in the DSE app; selections are persisted via PersistentState on
 * the backend, transparent to the user.
 */
export function ServiceSelect(props: Props<string[]> & { category: string }) {
  const ref = React.createRef<HTMLInputElement>();
  React.useEffect(() => {
    const { current } = ref;
    if (current) current.focus();
  });

  const { waitingForShareToken } = useShareTokenJwt();
  const { data, loading } = useAvailableServicesForCategoryQuery(props.category);

  const options = useMemo<ISelectOption[]>(() => {
    const services = data?.availableServicesForCategory ?? [];
    return services.map((s) => ({ value: s.serviceName, label: s.serviceName, origLabel: s.serviceName }));
  }, [data]);

  function onOptionChange({ target }: React.ChangeEvent<HTMLInputElement>) {
    const newValue = props.value.filter((value) => value !== target.value);
    if (target.checked) {
      newValue.push(target.value);
    }
    props.onChange(newValue);
  }

  const selected = useMemo(
    () => options.filter((option) => props.value.indexOf(option.value) !== -1),
    [props.value, options]
  );

  if ((loading || waitingForShareToken) && options.length === 0) {
    return <div className="flex flex-col gap-7" />;
  }

  return (
    <div className="flex flex-col gap-7 ">
      <ScanPrefillBanner category={props.category} value={props.value} onChange={props.onChange} />
      {options.length > 9 ? (
        <Autocomplete
          multiple
          options={options}
          value={selected}
          onChange={(_, value) => props.onChange(value.map((o) => o.value))}
          disabled={props.disabled}
        />
      ) : (
        options.map((option) => (
          <Tickbox
            key={option.value}
            checked={props.value.indexOf(option.value) !== -1}
            label={option.label}
            onChange={onOptionChange}
            type="checkbox"
            value={option.value}
          />
        ))
      )}
      <SuggestServicePanel category={props.category} value={props.value} onChange={props.onChange} />
    </div>
  );
}
